import Config from 'services/config/config';
import vendorOnboardingHttp from './http';

const V2_API_PREFIX = '/api/v2/procurement/vendor-onboarding/';

const assertVendorOnboardingApiUrl = (url, action) => {
  if (!url || !String(url).includes(V2_API_PREFIX)) {
    throw new Error(
      `Invalid vendor onboarding ${action} URL. Expected ${V2_API_PREFIX} API endpoint.`
    );
  }
};

const parseResponse = (res) => {
  const body = res?.data;
  const nested = body?.response;

  if (Number(body?.status) === 0 || body?.status === true) {
    if (nested && nested.status === false) {
      return {
        success: false,
        data: nested.data ?? null,
        message: nested.message || body.message || 'Request failed',
        errors: nested.errors || body.errors,
      };
    }
    return {
      success: true,
      data: body.data ?? nested?.data ?? body,
      message: body.message || nested?.message || '',
    };
  }
  if (nested?.status === true || Number(nested?.status) === 0) {
    return {
      success: true,
      data: nested.data,
      message: nested.message || body.message || '',
    };
  }
  const errors = body?.errors || nested?.errors;
  let message = body?.message || nested?.message || 'Request failed';
  if (errors && typeof errors === 'object') {
    const flat = Object.values(errors).flat().filter(Boolean);
    if (flat.length) message = flat.join(' ');
  }
  return { success: false, data: null, message, errors };
};

const parseError = (error, fallback = 'Request failed') => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 419) {
    return {
      success: false,
      data: null,
      message:
        'Invalid session or CSRF token. This form must use the public vendor onboarding API — please open the link from your email and try again.',
    };
  }

  if (typeof data === 'string' && /csrf|token mismatch/i.test(data)) {
    return {
      success: false,
      data: null,
      message:
        'Invalid CSRF token. Please use the vendor onboarding link from your email (React form), not the legacy Laravel form URL.',
    };
  }

  if (data && typeof data === 'object') {
    return parseResponse({ data });
  }

  return { success: false, data: null, message: error?.message || fallback };
};

export const fetchCreateMeta = async (firmName, name, email, tokenUserId) => {
  const url = Config.vendorOnboarding.createMeta(firmName, name, email, tokenUserId);
  assertVendorOnboardingApiUrl(url, 'create-meta');
  try {
    const res = await vendorOnboardingHttp.get(url);
    return parseResponse(res);
  } catch (error) {
    return parseError(error, 'Failed to load onboarding form');
  }
};

export const fetchEditMeta = async (vendorId) => {
  const url = Config.vendorOnboarding.editMeta(vendorId);
  assertVendorOnboardingApiUrl(url, 'edit-meta');
  try {
    const res = await vendorOnboardingHttp.get(url);
    return parseResponse(res);
  } catch (error) {
    return parseError(error, 'Failed to load vendor details');
  }
};

export const validateVendorGst = async (gstNo) => {
  const url = Config.vendorOnboarding.validateGst(gstNo);
  assertVendorOnboardingApiUrl(url, 'gst-validate');
  try {
    const res = await vendorOnboardingHttp.get(url);
    return parseResponse(res);
  } catch (error) {
    return parseError(error, 'GST validation failed');
  }
};

export const fetchGeoCities = async (stateId) => {
  if (!stateId) return { success: true, data: [] };
  try {
    const res = await vendorOnboardingHttp.get(
      `${Config.vendorOnboarding.fetchGeoCity}?geo_state_id=${stateId}`
    );
    const parsed = parseResponse(res);
    const list =
      parsed.data?.data ||
      parsed.data?.cities ||
      parsed.data ||
      res?.data?.response?.data ||
      [];
    return { success: true, data: Array.isArray(list) ? list : [] };
  } catch (error) {
    return parseError(error);
  }
};

export const fetchGeoPincodes = async (cityId) => {
  if (!cityId) return { success: true, data: [] };
  try {
    const res = await vendorOnboardingHttp.get(
      `${Config.vendorOnboarding.fetchGeoPincode}?geo_city_id=${cityId}`
    );
    const parsed = parseResponse(res);
    const list =
      parsed.data?.data ||
      parsed.data?.pincodes ||
      parsed.data ||
      res?.data?.response?.data ||
      [];
    return { success: true, data: Array.isArray(list) ? list : [] };
  } catch (error) {
    return parseError(error);
  }
};

export const submitVendorOnboarding = async (formData) => {
  const url = Config.vendorOnboarding.submit;
  assertVendorOnboardingApiUrl(url, 'submit');
  try {
    const res = await vendorOnboardingHttp.post(url, formData);
    return parseResponse(res);
  } catch (error) {
    return parseError(error, 'Failed to submit form');
  }
};

export const updateVendorOnboarding = async (formData) => {
  const url = Config.vendorOnboarding.update;
  assertVendorOnboardingApiUrl(url, 'update');
  try {
    const res = await vendorOnboardingHttp.post(url, formData);
    return parseResponse(res);
  } catch (error) {
    return parseError(error, 'Failed to update form');
  }
};
